/**
 * `SidebarUtility` — the bottom-of-sidebar footer. Hosts the four
 * "utility" affordances that historically lived in the retired `Header`
 * (the chunky top bar): a command-palette trigger, the gateway-status
 * indicator, a theme toggle, and the Clerk `<UserButton />`.
 *
 * Each row uses the same icon-only-when-collapsed / icon+label-when-
 * expanded shape as the `NavRow` items above it, so the column reads
 * as one continuous rail.
 *
 *   - **Search** — opens the command palette (Ctrl/⌘+K). Trailing `<kbd>`
 *     hint when expanded.
 *   - **Gateway** — runtime-status pill. Glyph + colour reflect the live
 *     runtime lifecycle (Feature C); hover shows the message, click pins a
 *     details popover (dismissed via the × button, Escape, or an outside
 *     click). Tooltip carries the verbose error string when present.
 *   - **Theme** — light/dark quick-toggle. "System" stays reachable from
 *     `/settings` only (matches the retired Header's behaviour).
 *   - **User** — Clerk `<UserButton />` + displayName. Only this row
 *     short-circuits to `null` when `VITE_CLERK_PUBLISHABLE_KEY` is
 *     absent (local dev without Clerk); the other three rows render
 *     regardless so the gateway pill + theme toggle stay reachable.
 */

import {
  Activity,
  AlertTriangle,
  Loader2,
  Moon,
  Pause,
  Power,
  Search,
  Sun,
  Unplug,
  Wifi,
  WifiOff,
  Wrench,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { UserButton } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";
import { useCommandPaletteStore } from "@/stores/useCommandPaletteStore";
import { useGatewayStore, type RuntimeState } from "@/stores/useGatewayStore";
import {
  selectHasActiveMissions,
  useMissionsStore,
} from "@/stores/useMissionsStore";
import { useEffectiveTheme, useThemeStore } from "@/stores/useThemeStore";
import { useUIStore } from "@/stores/useUIStore";
import { useMe } from "@/hooks/useMe";
import { clerkUserMenuAppearance } from "@/components/shell/clerk-appearance";
import { resolveGatewayDisplay } from "@/lib/gateway-display";
import { cn } from "@/lib/utils";

const clerkPk = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

/** Connection-state → compact i18n label (`header.gateway.*`). The
 *  full-sentence voice of `errors.gateway.*` is reserved for the
 *  disconnected banner; the sidebar row is icon + one-word state. */

interface SidebarUtilityProps {
  expanded: boolean;
}

export function SidebarUtility({ expanded }: SidebarUtilityProps) {
  return (
    <div
      data-testid="sidebar-utility"
      data-expanded={expanded}
      className={cn(
        "flex flex-col gap-0.5 border-t border-cream-200/60 py-2 dark:border-coffee-700/60",
        expanded ? "px-2" : "px-1",
      )}
    >
      <SearchRow expanded={expanded} />
      <AgentsRow expanded={expanded} />
      {/* GatewayRow MOVED to the chat infobar 2026-07-30 (operator) — the
          connector now renders as a status-bar segment. See
          `components/chat/ChatInfobarIndicators.tsx`; the shared state
          resolver lives at `lib/gateway-display.ts`. */}
      <ThemeRow expanded={expanded} />
      <UserRow expanded={expanded} />
    </div>
  );
}

// Shared row class — mirrors `navRowClass` in `ActivityBar.tsx`. Kept
// inline (rather than imported) so the footer doesn't coupling-import
// from the nav module; both modules render against the same Tailwind
// token vocabulary so visual drift would be obvious in review.
function utilityRowClass(expanded: boolean): string {
  return cn(
    "flex items-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 dark:focus-visible:ring-offset-coffee-900",
    expanded ? "h-9 w-full gap-2 px-3 text-body-sm" : "h-9 w-9 justify-center",
    "text-coffee-500 hover:bg-cream-100/60 hover:text-coffee-700 dark:text-cream-500 dark:hover:bg-coffee-800/60 dark:hover:text-cream-300",
  );
}

function useIsMacPlatform() {
  const [isMac, setIsMac] = useState(false);
  useEffect(() => {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    setIsMac(/Mac|iPhone|iPad/i.test(ua));
  }, []);
  return isMac;
}

function SearchRow({ expanded }: { expanded: boolean }) {
  const { t } = useTranslation();
  const openPalette = useCommandPaletteStore((s) => s.setOpen);
  const isMac = useIsMacPlatform();
  const shortcut = isMac ? "⌘K" : "Ctrl+K";
  const label = t("header.search");
  return (
    <button
      type="button"
      onClick={() => openPalette(true)}
      title={t("header.commandPaletteTitle", { shortcut })}
      aria-label={t("header.commandPaletteAriaLabel")}
      data-testid="sidebar-utility-search"
      className={utilityRowClass(expanded)}
    >
      <Search size={20} aria-hidden />
      {expanded && (
        <>
          <span className="flex-1 truncate text-left">{label}</span>
          <kbd className="shrink-0 rounded border border-cream-200 bg-cream-50 px-1 font-mono text-[10px] text-coffee-500 dark:border-coffee-700 dark:bg-coffee-800 dark:text-cream-500">
            {shortcut}
          </kbd>
        </>
      )}
    </button>
  );
}

function GatewayRow({ expanded }: { expanded: boolean }) {
  const { t } = useTranslation();
  const connectionState = useGatewayStore((s) => s.connectionState);
  const runtimeState = useGatewayStore((s) => s.runtimeState);
  const repairInProgress = useGatewayStore((s) => s.repairInProgress);
  const gatewayError = useGatewayStore((s) => s.error);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Dismissal: the pinned popover is a transient read, so it honours the
  // same affordances every other popover in the app does — Escape, an
  // outside click/tap, and an explicit close (×) button (below). Without
  // these the only way to close it is to re-click the trigger, which isn't
  // discoverable and leaves the panel feeling "stuck" open.
  useEffect(() => {
    if (!popoverOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPopoverOpen(false);
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setPopoverOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [popoverOpen]);

  // Feature C — the icon's glyph AND colour reflect the runtime lifecycle
  // (merged with the WS state). Hover shows the message (title); click pins
  // a details popover.
  const d = resolveGatewayDisplay(connectionState, runtimeState, repairInProgress);
  const Icon = d.Icon;
  const stateLabel = t(d.labelKey);
  const message = t(d.msgKey);
  const tooltip = gatewayError ? `${message} (${gatewayError})` : message;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        role="status"
        data-testid="sidebar-utility-gateway"
        data-connection-state={connectionState}
        data-runtime-state={runtimeState ?? undefined}
        aria-label={tooltip}
        aria-expanded={popoverOpen}
        title={tooltip}
        onClick={() => setPopoverOpen((o) => !o)}
        className={cn(
          utilityRowClass(expanded),
          "cursor-pointer hover:bg-cream-100/60 dark:hover:bg-coffee-800/60",
          d.colour,
        )}
      >
        <Icon size={20} aria-hidden className={d.spin ? "animate-spin" : undefined} />
        {expanded && (
          <span className="truncate text-left text-body-sm">{stateLabel}</span>
        )}
      </button>
      {popoverOpen && (
        <div
          role="tooltip"
          data-testid="runtime-status-popover"
          className="absolute bottom-full left-0 z-50 mb-1 w-64 rounded-md border border-cream-200 bg-cream-50 p-3 text-body-sm shadow-lg dark:border-coffee-700 dark:bg-coffee-800"
        >
          <div className="flex items-start justify-between gap-2">
            <p className={cn("font-semibold", d.colour)}>{stateLabel}</p>
            <button
              type="button"
              onClick={() => setPopoverOpen(false)}
              aria-label={t("actions.close")}
              data-testid="runtime-status-popover-close"
              className="-mr-1 -mt-1 shrink-0 rounded p-0.5 text-coffee-500 transition-colors hover:bg-cream-100 hover:text-coffee-700 dark:text-cream-500 dark:hover:bg-coffee-700 dark:hover:text-cream-200"
            >
              <X size={14} aria-hidden />
            </button>
          </div>
          <p className="mt-0.5 text-coffee-600 dark:text-cream-300">{message}</p>
          {gatewayError && (
            <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{gatewayError}</p>
          )}
        </div>
      )}
    </div>
  );
}

function ThemeRow({ expanded }: { expanded: boolean }) {
  const { t } = useTranslation();
  const effective = useEffectiveTheme();
  const setPref = useThemeStore((s) => s.setPref);
  const isDark = effective === "dark";
  // Action-oriented copy matches the retired Header — the row reads as
  // a quick-toggle ("Switch to light theme"), not a passive label. The
  // icon shows the destination state (Sun → flipping to light; Moon →
  // flipping to dark) so glance-only readers still get the right cue.
  const label = isDark ? t("header.themeSwitchToLight") : t("header.themeSwitchToDark");
  return (
    <button
      type="button"
      onClick={() => setPref(isDark ? "light" : "dark")}
      title={label}
      aria-label={label}
      data-testid="sidebar-utility-theme"
      className={utilityRowClass(expanded)}
    >
      {isDark ? <Sun size={20} aria-hidden /> : <Moon size={20} aria-hidden />}
      {expanded && <span className="truncate text-left">{label}</span>}
    </button>
  );
}

function UserRow({ expanded }: { expanded: boolean }) {
  const { t } = useTranslation();
  const effective = useEffectiveTheme();
  const me = useMe();
  const userLabel =
    me.data?.displayName ??
    me.data?.firstName ??
    t("header.userMenuAriaLabel");

  // The avatar is owned by Clerk; without a publishable key it can't
  // mount, so this row short-circuits while the rest of the utility
  // block stays reachable for local-dev-without-Clerk operators.
  if (!clerkPk) return null;

  return (
    <div
      data-testid="sidebar-utility-user"
      className={cn(
        "mt-1 flex items-center",
        // The UserButton's avatar is ~28px tall (Clerk default) — it
        // breaks the 36px row rhythm above, but Clerk renders inside a
        // portal so we can't restyle it. Cap row height to keep
        // collapsed/expanded vertical layouts predictable.
        expanded ? "gap-2 px-3 py-1" : "justify-center py-1",
      )}
    >
      <UserButton afterSignOutUrl="/" appearance={clerkUserMenuAppearance(effective)} />
      {expanded && (
        <span className="min-w-0 flex-1 truncate text-body-sm text-coffee-700 dark:text-cream-300">
          {userLabel}
        </span>
      )}
    </div>
  );
}

/**
 * `AgentsRow` — toggles the right-side Lexiun Agents pane open. Sits
 * alongside the other utility chips (Search / Gateway / Theme / User)
 * so the lawyer has one place to find every shell affordance, replacing
 * the previous standalone right-rail / floating-button overlay that
 * collided with case-header content (operator-flagged 2026-06-26).
 *
 * Live state badge: when missions are actively running (see
 * `selectHasActiveMissions`), the icon pulses + tints gold so the
 * lawyer can see at a glance that the team is working — even when
 * the Agents pane is collapsed. Click toggles the pane.
 */
function AgentsRow({ expanded }: { expanded: boolean }) {
  const { t } = useTranslation();
  const setRightSidebarExpanded = useUIStore(
    (s) => s.setRightSidebarExpanded,
  );
  const rightSidebarExpanded = useUIStore((s) => s.rightSidebarExpanded);
  const hasActive = useMissionsStore(selectHasActiveMissions);
  const ariaLabel = t("rightSidebar.expandAriaLabel");
  return (
    <button
      type="button"
      data-testid="sidebar-utility-agents"
      data-has-active={hasActive ? "true" : undefined}
      data-pane-expanded={rightSidebarExpanded ? "true" : undefined}
      onClick={() => setRightSidebarExpanded(!rightSidebarExpanded)}
      title={ariaLabel}
      aria-label={ariaLabel}
      aria-pressed={rightSidebarExpanded}
      className={cn(
        utilityRowClass(expanded),
        // Reflect active state via colour + a subtle pulse. The base
        // utilityRowClass owns hover; we override the idle colour when
        // missions are running so the cue stays loud across themes.
        hasActive &&
          "text-gold-600 hover:text-gold-700 dark:text-gold-400 dark:hover:text-gold-300",
      )}
    >
      <Activity
        size={expanded ? 16 : 18}
        className={cn("shrink-0", hasActive && "animate-pulse")}
      />
      {expanded && (
        <span className="min-w-0 flex-1 truncate text-left">
          {t("rightSidebar.title")}
        </span>
      )}
    </button>
  );
}
